import { useTheme } from '@emotion/react'
import { ConfigurationContext } from '@news-mono/web-common'
import React, { FC, useContext, useEffect } from 'react'
import { useHistory } from 'react-router'
import { Skeleton } from '../../../src/content/Skeleton/Skeleton'
import { themedValue } from '../../__styling'
import { StyledWebLink } from '../EmailPreference.styled'
import { useSingleUnsubscribe } from '../hooks/useUnsubscribe'
import { UnsubscribeParam, UnsubscribeStatus } from '../pages/Unsubscribe'
import { EmailPreferenceItem } from './PreferenceSection'
import { StyledBody, StyledButton, StyledInfoLine } from './Unsubscribe.styled'

interface UnsubscribeBodyProp {
    subscribeAccount?: EmailPreferenceItem
    useProfileEmail: boolean
    tokens?: UnsubscribeParam['tokens']
    status: UnsubscribeStatus
    updateStatus: (status: UnsubscribeStatus) => void
}

interface BodyMeta {
    infoLine: string
    cancelBtnText: string
    cancelBtnLink: string
}

export const UnsubscribeBody: FC<UnsubscribeBodyProp> = ({
    subscribeAccount,
    useProfileEmail,
    tokens,
    status,
    updateStatus,
}) => {
    const theme = useTheme()
    const contactLink = themedValue(theme, {
        thewest: '/contact',
        perthnow: '/contact-us',
        sevennews: '/contact-us',
        fallback: '/contact',
    })
    const { emailServiceApi } = useContext(ConfigurationContext)
    const token = useProfileEmail ? tokens?.profileToken : tokens?.urlToken
    const history = useHistory()
    const unsubscribe = useSingleUnsubscribe(
        {
            account: subscribeAccount?.subAccount,
            listId: subscribeAccount?.guid,
            toggleState: 'off',
            unsubscribeGroupId: subscribeAccount?.unSubId,
        },
        token,
        emailServiceApi,
    )
    const redirectToEpcLink = tokens?.urlToken
        ? `/manage-email-preferences?token=${tokens.urlToken}`
        : '/manage-email-preferences'
    useEffect(() => {
        if (unsubscribe.isError) {
            updateStatus('error')
        }

        if (unsubscribe.isSuccess) {
            updateStatus('success')
        }
    }, [unsubscribe.isError, unsubscribe.isSuccess, updateStatus])

    function onClickUnsubscribe() {
        !unsubscribe.isLoading && unsubscribe.mutate()
    }

    const bodyMeta: Record<UnsubscribeStatus, BodyMeta> = {
        initial: {
            infoLine: 'You are unsubscribing from:',
            cancelBtnText: 'Cancel',
            cancelBtnLink: redirectToEpcLink,
        },
        error: {
            infoLine: 'There was an error processing your unsubscribe request.',
            cancelBtnText: 'Try again',
            cancelBtnLink: redirectToEpcLink,
        },
        success: {
            infoLine: 'You have successfully unsubscribed from:',
            cancelBtnText: 'Manage email preferences',
            cancelBtnLink: redirectToEpcLink,
        },
    }

    function onClickCancel() {
        if (status === 'error') {
            history.go(0)
        } else {
            history.push(bodyMeta[status].cancelBtnLink)
        }
    }

    return (
        <StyledBody>
            <StyledInfoLine hasError={status === 'error'}>
                {bodyMeta[status].infoLine}
            </StyledInfoLine>
            {status !== 'error' && (
                <h3>
                    {subscribeAccount?.name}
                    {!token && <Skeleton height={35} width={220} />}
                </h3>
            )}
            {status === 'initial' && (
                <StyledButton
                    isPrimary={true}
                    isLoading={!token || unsubscribe.isLoading}
                    onClick={onClickUnsubscribe}
                >
                    Unsubscribe
                </StyledButton>
            )}
            {status === 'error' && (
                <p>
                    Please try again. If you still cannot unsubscribe, please
                    <StyledWebLink display="inline" to={contactLink}>
                        &nbsp;contact us&nbsp;
                    </StyledWebLink>
                    so we can resolve this for you.
                </p>
            )}
            <StyledButton onClick={onClickCancel} status={status}>
                {bodyMeta[status].cancelBtnText}
            </StyledButton>
            {status === 'initial' && (
                <StyledWebLink to={redirectToEpcLink} onClick={onClickCancel}>
                    Manage email preferences
                </StyledWebLink>
            )}
        </StyledBody>
    )
}
